import type { RepairItem, QuoteRequest, QuoteSubmission } from '@/util/types/serverTypeCollection/repairList';
import type { RepairCompanyRaw } from '@/util/types/serverTypeCollection/repairCompanies';
import { isQwikFixQuoteSubmission } from '@/util/functions/repairList/isQwikFixQuoteSubmission';
import { buildUnifiedQuoteEntries } from '@/app/client/components/repair-list-quote-entries/quoteEntryUtils';
import type { ReportSettingsType } from '@/util/types/serverTypeCollection/reportSettings';
import { resolveRepairQuoteExperience } from '@/util/functions/reportSettings/resolveRepairQuoteExperience';
import type { Report } from '@/app/client/reports/types';
import {
  repairItemName,
  repairItemRecommendationId,
  repairItemThumbUrl,
} from '@/app/client/reports/utils/repairItemAccessors';
import type {
  QuickFixRepairItem,
  QuickFixSpecialtyContractor,
  QuickFixSpecialtyContractorGroup,
  QuickFixThumbVariant,
} from './types';

const THUMB_VARIANTS: QuickFixThumbVariant[] = ['t1', 't2', 't3', 't4'];

const SPECIALTY_LOGO_COLORS = ['#1e3a8a', '#7c2d12', '#065f46', '#01445A'];
const DEV_INCLUDE_DEFAULT_ALL_REPAIRS_COMPANIES_IN_QWIKFIX_MATCHING = false;

export type RepairQuoteIntegrationMode = 'propair' | 'qwikfix' | 'legacy';

export type SentQuoteRecipient = {
  id: string;
  name: string;
  itemCount: number;
};

function formatSubmissionRecipientName(submission: QuoteSubmission): string {
  const category = submission.categoryId?.trim().replace(/_/g, ' ');

  if (isQwikFixQuoteSubmission(submission)) return 'QwikFix';
  if (submission.submissionType === 'thumbtack') {
    return category ? `${category} (Thumbtack)` : 'Thumbtack';
  }

  const typeLabel =
    submission.submissionType.charAt(0).toUpperCase() + submission.submissionType.slice(1);
  return category ? `${category} - ${typeLabel}` : typeLabel;
}

/** Recipients already persisted on the repair list (quoteSubmissions + quoteRequests). */
export function buildSentQuoteRecipientsFromRepairList(
  quoteSubmissions: QuoteSubmission[] = [],
  quoteRequests: QuoteRequest[] = [],
  repairCompanies: RepairCompanyRaw[] = [],
): SentQuoteRecipient[] {
  const recipients: SentQuoteRecipient[] = [];

  for (const submission of quoteSubmissions) {
    const itemCount = submission.includedItemIds?.length || 0;
    if (itemCount === 0) continue;

    recipients.push({
      id: `submission-${submission._id || String(submission.submittedAt)}`,
      name: formatSubmissionRecipientName(submission),
      itemCount,
    });
  }

  for (const quote of quoteRequests) {
    const itemCount = quote.repairItemIds?.length || 0;
    if (itemCount === 0) continue;

    const company = repairCompanies.find((c) => c._id === quote._repairCompanyId);
    recipients.push({
      id: `request-${quote._id || String(quote.requestedAt)}`,
      name: company?.name || 'Unknown Company',
      itemCount,
    });
  }

  return recipients;
}

export function hasPersistedQuoteActivity(
  quoteSubmissions: QuoteSubmission[] = [],
  quoteRequests: QuoteRequest[] = [],
): boolean {
  return buildUnifiedQuoteEntries({ quoteSubmissions, quoteRequests }).length > 0;
}

/** Deficiency observations on the inspection report (same pool as repair-list adds). */
export function countReportDeficiencyObservations(
  report?: Pick<Report, 'sections'>,
): number {
  if (!report?.sections?.length) return 0;
  return report.sections.reduce(
    (sectionTotal, section) =>
      sectionTotal +
      section.items.reduce(
        (itemTotal, item) =>
          itemTotal +
          item.observations.filter((obs) => obs.comment_type === 'defect').length,
        0,
      ),
    0,
  );
}

export function getRepairQuoteIntegrationMode(
  reportSettings?: Pick<
    ReportSettingsType,
    'propairExperience' | 'proPairExperience' | 'qwikFixExperience'
  >,
): RepairQuoteIntegrationMode {
  const { propairExperience, qwikFixEnabled } =
    resolveRepairQuoteExperience(reportSettings);

  if (propairExperience === 'v1' || propairExperience === 'v2') return 'propair';
  if (qwikFixEnabled) return 'qwikfix';
  return 'legacy';
}

function getCompanyInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function isDefaultAllRepairsCompany(company: RepairCompanyRaw): boolean {
  return (company.recommendedIds || []).includes('*');
}

function getQwikFixSpecialtyRepairCompanies(
  repairCompanies: RepairCompanyRaw[],
): RepairCompanyRaw[] {
  if (DEV_INCLUDE_DEFAULT_ALL_REPAIRS_COMPANIES_IN_QWIKFIX_MATCHING) {
    return repairCompanies;
  }

  return repairCompanies.filter((company) => !isDefaultAllRepairsCompany(company));
}

/** All non-wildcard recommendation IDs assigned to repair companies in settings. */
export function getSpecialtyRecommendationIds(repairCompanies: RepairCompanyRaw[]): Set<string> {
  const ids = new Set<string>();
  getQwikFixSpecialtyRepairCompanies(repairCompanies).forEach((company) => {
    (company.recommendedIds || []).filter((id) => id !== '*').forEach((id) => ids.add(id));
  });
  return ids;
}

export function isRepairItemCategorizedByRepairCompany(
  item: RepairItem,
  repairCompanies: RepairCompanyRaw[],
): boolean {
  const recommendationId = repairItemRecommendationId(item);
  if (!recommendationId) return false;
  return getSpecialtyRecommendationIds(repairCompanies).has(String(recommendationId));
}

/** Items included on the QwikFix catch-all quote for the current scope. */
export function getQwikFixScopedItems(
  items: QuickFixRepairItem[],
  quoteWholeReport: boolean,
): QuickFixRepairItem[] {
  if (quoteWholeReport) return items;
  return items.filter((item) => !item.isCategorizedByRepairCompany);
}

export function mapRepairItemsToQuickFixItems(
  repairItems: RepairItem[],
  repairCompanies: RepairCompanyRaw[] = [],
): QuickFixRepairItem[] {
  const specialtyIds = getSpecialtyRecommendationIds(repairCompanies);

  return repairItems.map((item, index) => {
    const recommendationId = repairItemRecommendationId(item);
    const recommendationIdStr =
      recommendationId != null ? String(recommendationId) : undefined;

    return {
      id: item.id,
      title: repairItemName(item),
      section: item.sectionName,
      recommendationId: recommendationIdStr,
      isCategorizedByRepairCompany: recommendationIdStr
        ? specialtyIds.has(recommendationIdStr)
        : false,
      thumbUrl: repairItemThumbUrl(item),
      thumbVariant: THUMB_VARIANTS[index % THUMB_VARIANTS.length],
    };
  });
}

/** Categorized items assigned to the first matching specialty repair company. */
export function groupItemsBySpecialtyContractor(
  items: QuickFixRepairItem[],
  contractors: QuickFixSpecialtyContractor[],
): Record<string, QuickFixRepairItem[]> {
  const assignments: Record<string, QuickFixRepairItem[]> = {};
  contractors.forEach((contractor) => {
    assignments[contractor.id] = [];
  });

  items.forEach((item) => {
    if (!item.recommendationId || !item.isCategorizedByRepairCompany) return;
    const contractor = contractors.find((c) => c.recommendedIds.includes(item.recommendationId!));
    if (contractor) {
      assignments[contractor.id].push(item);
    }
  });

  return assignments;
}

export function buildSpecialtyCompanyAssignments(
  groups: QuickFixSpecialtyContractorGroup[],
  contractorsSent: Record<string, boolean> = {},
): Record<string, string[]> {
  return Object.fromEntries(
    groups
      .filter(({ contractor }) => !contractorsSent[contractor.id])
      .map(({ contractor, items: contractorItems }) => [
        contractor.id,
        contractorItems.map((item) => item.id),
      ]),
  );
}

/** Companies that would receive a quote on a full send (QwikFix + specialty with items). */
export function getQuoteRecipientCompanyCount(
  qwikItemCount: number,
  specialtyGroupCount: number,
  options?: { includeQwikFix?: boolean },
): number {
  const includeQwikFix = options?.includeQwikFix ?? qwikItemCount > 0;
  return (includeQwikFix && qwikItemCount > 0 ? 1 : 0) + specialtyGroupCount;
}

export function hasPendingQuoteRequests({
  qwikItems,
  qwikSent,
  specialtyContractorGroups,
  contractorsSent,
}: {
  qwikItems: QuickFixRepairItem[];
  qwikSent: boolean;
  specialtyContractorGroups: QuickFixSpecialtyContractorGroup[];
  contractorsSent: Record<string, boolean>;
}): boolean {
  const pendingQwik = qwikItems.length > 0 && !qwikSent;
  const pendingSpecialty = specialtyContractorGroups.some(
    ({ contractor }) => !contractorsSent[contractor.id],
  );
  return pendingQwik || pendingSpecialty;
}

export function getSpecialtyContractorGroups(
  items: QuickFixRepairItem[],
  repairCompanies: RepairCompanyRaw[],
  specialtyContractors?: QuickFixSpecialtyContractor[],
): QuickFixSpecialtyContractorGroup[] {
  const contractors =
    specialtyContractors && specialtyContractors.length > 0
      ? specialtyContractors
      : mapRepairCompaniesToSpecialtyContractors(repairCompanies);

  const assignments = groupItemsBySpecialtyContractor(items, contractors);

  return contractors
    .filter((contractor) => (assignments[contractor.id]?.length ?? 0) > 0)
    .map((contractor) => ({
      contractor,
      items: assignments[contractor.id],
    }));
}

/** Specialty contractors only — excludes catch-all wildcard (`*`) companies. */
export function mapRepairCompaniesToSpecialtyContractors(
  repairCompanies: RepairCompanyRaw[],
): QuickFixSpecialtyContractor[] {
  return getQwikFixSpecialtyRepairCompanies(repairCompanies)
    .filter((company) => {
      const ids = company.recommendedIds || [];
      const specialtyIds = ids.filter((id) => id !== '*');
      return specialtyIds.length > 0;
    })
    .map((company, index) => ({
      id: company._id,
      name: company.name,
      initials: getCompanyInitials(company.name),
      color: SPECIALTY_LOGO_COLORS[index % SPECIALTY_LOGO_COLORS.length],
      logoUrl: company.logo || undefined,
      email: company.email,
      phone: company.phone,
      responseTime: company.averageQuoteTime,
      specialties: company.specialty || [],
      recommendedIds: (company.recommendedIds || []).filter((id) => id !== '*'),
    }));
}
